
// src/components/BlogDetail.jsx
import React, { useState, useEffect } from 'react';
import CommonHeader from './CommonHeader';
import CommonFooter from './CommonFooter';
import config from '../config';
import './BlogDetail.css';

const BlogDetail = ({ slug, onBack, onBlogClick, onLoginClick, onSignupClick, onNavigateToHome }) => {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [subscribeError, setSubscribeError] = useState('');
  const [recentPosts, setRecentPosts] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(false);

  useEffect(() => {
    if (slug && typeof slug === 'string') {
      fetchBlogPost();
      fetchRecentPosts(); // Add this line
    }
    window.scrollTo(0, 0);
  }, [slug]);

  const fetchBlogPost = async () => {
    try {
      setLoading(true);
      const url = `${config.API_BASE_URL}/api/blog/${slug}/`;
      // console.log('Fetching URL:', url);  // Debug
      const response = await fetch(url);
      if (!response.ok) throw new Error('Post not found');
      console.log('Response status:', response.status);  // Debug
      const data = await response.json();
      setPost(data);
      document.title = `${data.meta_title || data.title} | {config.APP_NAME} Blog`;
    } catch (error) {
      // console.error('Error fetching blog post:', error);
      setError('Blog post not found');
    } finally {
      setLoading(false);
    }
  };

  // Fetch recent posts (excluding current post)
  const fetchRecentPosts = async () => {
    try {
      setLoadingRecent(true);
      const response = await fetch(`${config.API_BASE_URL}/api/blog/`);
      if (!response.ok) throw new Error('Failed to fetch recent posts');
      const data = await response.json();

      // Filter out current post and get top 5 most recent
      const filtered = data
        .filter(post => post.slug !== slug) // Exclude current post
        .slice(0, 5); // Get top 5

      setRecentPosts(filtered);
    } catch (error) {
      console.error('Error fetching recent posts:', error);
    } finally {
      setLoadingRecent(false);
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;

    setSubscribing(true);
    setSubscribeError('');

    try {
      const response = await fetch(`${config.API_BASE_URL}/api/subscribe/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email }),
      });

      const data = await response.json();

      if (response.ok) {
        setSubscribed(true);
        setEmail('');
        setTimeout(() => setSubscribed(false), 3000);
      } else {
        setSubscribeError(data.error || data.message || 'Subscription failed. Please try again.');
        setTimeout(() => setSubscribeError(''), 3000);
      }
    } catch (error) {
      console.error('Subscription error:', error);
      setSubscribeError('Network error. Please try again.');
      setTimeout(() => setSubscribeError(''), 3000);
    } finally {
      setSubscribing(false);
    }
  };


  // Navigation handler for header and footer
  const handleNavigate = (page) => {
    console.log('Navigating to:', page);

    switch (page) {
      case 'home':
        if (onNavigateToHome) {
          onNavigateToHome();
        }
        break;
      case 'features':
        if (onNavigateToHome) {
          onNavigateToHome();
          setTimeout(() => {
            const featuresSection = document.getElementById('features');
            if (featuresSection) {
              featuresSection.scrollIntoView({ behavior: 'smooth' });
            }
          }, 100);
        }
        break;
      case 'how-it-works':
        if (onNavigateToHome) {
          onNavigateToHome();
          setTimeout(() => {
            const howItWorksSection = document.getElementById('how-it-works');
            if (howItWorksSection) {
              howItWorksSection.scrollIntoView({ behavior: 'smooth' });
            }
          }, 100);
        }
        break;
      case 'pricing':
        if (onNavigateToHome) {
          onNavigateToHome();
          setTimeout(() => {
            const pricingSection = document.getElementById('pricing');
            if (pricingSection) {
              pricingSection.scrollIntoView({ behavior: 'smooth' });
            }
          }, 100);
        }
        break;
      case 'blog':
        if (onBack) {
          onBack();
        }
        break;
      default:
        if (onNavigateToHome) {
          onNavigateToHome();
        }
        break;
    }
  };

  const handleBackToList = () => {
    if (onBack) {
      onBack();
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading article...</p>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="error-container">
        <div className="error-icon">📖</div>
        <h3>Article Not Found</h3>
        <p>The blog post you're looking for doesn't exist.</p>
        <button onClick={handleBackToList} className="back-btn">← Back to Blog</button>
      </div>
    );
  }

  return (
    <div className="blog-detail-page">
      <CommonHeader
        activePage="blog"
        onNavigate={handleNavigate}
        onLogin={onLoginClick}
        onSignup={onSignupClick}
      />

      <header className="blog-header">
        <div className="container">
          <button onClick={handleBackToList} className="back-to-blog">
            ← Back to Blog
          </button>
          <h1 className="blog-title">{post.title}</h1>
          <div className="blog-meta">
            <div className="meta-item">
              <span className="meta-icon">✍️</span>
              <span>{post.author}</span>
            </div>
            <div className="meta-item">
              <span className="meta-icon">📅</span>
              <span>{post.formatted_date}</span>
            </div>
            <div className="meta-item">
              <span className="meta-icon">⏱️</span>
              <span>{post.read_time} min read</span>
            </div>
            {/* <div className="meta-item">
              <span className="meta-icon">👁️</span>
              <span>{post.views} views</span>
            </div> */}
          </div>
          {post.tags && (
            <div className="blog-tags">
              {post.tags.split(',').map(tag => (
                <span key={tag} className="blog-tag">#{tag.trim()}</span>
              ))}
            </div>
          )}
        </div>
      </header>

      {/* Featured Image */}
      {/* {post.featured_image && (
        <div className="blog-featured-image">
          <div className="container">
            <img src={post.featured_image} alt={post.title} />
          </div>
        </div>
      )} */}
      {/* Featured Image Section */}
      {post.featured_image && (
        <div className="blog-featured-image">
          <div className="container">
            <img
              src={post.featured_image}
              alt={post.meta_title || post.title}
              loading="lazy"
              className="featured-img"
            />
            {post.image_caption && (
              <p className="image-caption">{post.image_caption || post.title}</p>
            )}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="blog-content">
        <div className="container">
          <div className="content-wrapper">
            <article className="post-content" dangerouslySetInnerHTML={{ __html: post.content }} />
            <button className="cta-button" onClick={() => onLoginClick()}>
              Create Your Form Now
            </button>
            {/* Share Section */}
            <div className="share-section">
              <h4>Share this article</h4>
              <div className="share-buttons">
                <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(window.location.href)}`}
                  target="_blank" rel="noopener noreferrer" className="share-btn twitter">
                  🐦 Twitter
                </a>
                <a href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(window.location.href)}&title=${encodeURIComponent(post.title)}`}
                  target="_blank" rel="noopener noreferrer" className="share-btn linkedin">
                  🔗 LinkedIn
                </a>
                <a href={`mailto:?subject=${encodeURIComponent(post.title)}&body=${encodeURIComponent(window.location.href)}`}
                  className="share-btn email">
                  📧 Email
                </a>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="blog-sidebar">
            {/* About Widget */}
            <div className="sidebar-widget">
              <h3>About {config.APP_NAME}</h3>
              <p>Create dynamic forms, fill the values in the corresponding fields, and generate professional PDFs instantly.</p>
              <button className="cta-button" onClick={onLoginClick}>
                Get Started Free
              </button>
            </div>

            {/* Newsletter Widget - Stay Updated */}
            <div className="sidebar-widget newsletter-widget">
              <h3>📧 Stay Updated</h3>
              <p>Get the latest articles delivered to your inbox</p>

              {subscribeError && (
                <div className="subscribe-error">
                  <span>⚠️</span> {subscribeError}
                </div>
              )}

              {subscribed ? (
                <div className="subscribe-success">
                  <span>✓</span>
                  <p>Thanks for subscribing!</p>
                </div>
              ) : (
                <form onSubmit={handleSubscribe} className="newsletter-form">
                  <input
                    type="email"
                    placeholder="Your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={subscribing}
                    className="newsletter-input"
                  />
                  <button type="submit" className="subscribe-btn" disabled={subscribing}>
                    {subscribing ? 'Subscribing...' : 'Subscribe'}
                  </button>
                </form>
              )}
              <p className="newsletter-note">No spam, unsubscribe anytime.</p>
            </div>


            {/* Recent Posts Widget */}
            <div className="sidebar-widget">
              <h3>📖 Recent Posts</h3>
              {loadingRecent ? (
                <div className="recent-posts-loading">
                  <div className="loading-spinner-small"></div>
                  <p>Loading recent posts...</p>
                </div>
              ) : recentPosts.length > 0 ? (
                <div className="recent-posts">
                  {recentPosts.map(post => (
                    <a
                      key={post.id}
                      href={`/blog/${post.slug}`}
                      onClick={(e) => {
                        e.preventDefault();
                        window.location.href = `/blog/${post.slug}`;
                      }}
                      className="recent-post-link"
                    >
                      <span className="recent-post-title">{post.title}</span>                    </a>
                  ))}
                </div>
              ) : (
                <p className="no-recent-posts">No recent posts available</p>
              )}
            </div>
          </aside>
        </div>
      </div>

      <CommonFooter onNavigate={handleNavigate} activePage="blog" />
    </div>
  );
};

export default BlogDetail;